import { initializeApp, getApps, type FirebaseApp } from 'firebase/app'
import { getAuth, connectAuthEmulator, type Auth } from 'firebase/auth'
import { getStorage, connectStorageEmulator, type FirebaseStorage } from 'firebase/storage'
import { getFirestore, type Firestore } from 'firebase/firestore'

// ─── Firebase config ──────────────────────────────────────────────────────────
// These values are safe to commit — they identify your Firebase project
// but do not grant access. Security is enforced by Firebase Auth + SQL Connect
// @auth rules, not by keeping these values secret.
//
// Replace with your actual project values.
// Copy from Firebase Console → Project Settings → Your apps → SDK setup.

const firebaseConfig = {
  apiKey:            import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain:        import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId:         import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket:     import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  // Root .env historically uses VITE_FIREBASE_APPID (no underscore)
  appId:             import.meta.env.VITE_FIREBASE_APP_ID ?? import.meta.env.VITE_FIREBASE_APPID,
}

// ─── Singleton initialisation ─────────────────────────────────────────────────
// Guard against double-initialisation in development HMR

let app: FirebaseApp

if (getApps().length === 0) {
  app = initializeApp(firebaseConfig)
} else {
  app = getApps()[0]
}

export { app }

// ─── Service exports ──────────────────────────────────────────────────────────
// Import these in your components instead of calling getAuth() / getStorage()
// directly — keeps initialisation in one place.

export const auth:    Auth            = getAuth(app)
export const storage: FirebaseStorage = getStorage(app)
export const db:      Firestore       = getFirestore(app)

// ─── Emulator wiring (local dev only) ─────────────────────────────────────────
// Gated by VITE_USE_EMULATORS=true so it is a no-op in normal dev/prod. Lets apps
// run against the Firebase emulators instead of the live project. Data Connect's
// emulator is wired per-app at the entry point (the DC client lives in the
// generated SDK, not here). Ports match firebase.json.
if (import.meta.env.VITE_USE_EMULATORS === 'true') {
  connectAuthEmulator(auth, 'http://localhost:9099', { disableWarnings: true })
  connectStorageEmulator(storage, 'localhost', 9199)
}

// ─── SQL Connect client ───────────────────────────────────────────────────────
// The SQL Connect generated SDK exports its own connector — re-export it here
// so both apps import from one place.
//
// After running `yarn sdk:generate` from the repo root, the generated SDK
// will be in packages/dataconnect-generated/. Update this import path
// once that package exists.
//
// export { connectorConfig } from '@classroom-ai/dataconnect-generated'

// ─── Auth helpers ─────────────────────────────────────────────────────────────

// Matches the SQL Connect UserRole enum and the values set in custom claims
export type UserRole = 'STUDENT' | 'SCHOOL_STAFF' | 'ED360_STAFF' | 'DISTRICT_ADMIN'

/**
 * Reads the custom role claim set by your Firebase Auth Cloud Function
 * when a user is provisioned. Returns null if no claim is set.
 */
export async function getUserRole(): Promise<UserRole | null> {
  const user = auth.currentUser
  if (!user) return null
  const token = await user.getIdTokenResult()
  return (token.claims.role as UserRole) ?? null
}

/**
 * Returns the classId claim set on the user's token.
 * Students and teachers both have a classId claim.
 */
export async function getUserClassId(): Promise<string | null> {
  const user = auth.currentUser
  if (!user) return null
  const token = await user.getIdTokenResult()
  return (token.claims.classId as string) ?? null
}

/**
 * Returns the districtId claim set on the user's token. Only DISTRICT_ADMIN users
 * carry this claim (minted by the assignRole callable); returns null otherwise.
 * It is the only server-side district-scoping mechanism (getDistrictRollup checks it).
 */
export async function getUserDistrictId(): Promise<string | null> {
  const user = auth.currentUser
  if (!user) return null
  const token = await user.getIdTokenResult()
  return (token.claims.districtId as string) ?? null
}
